import pkg from 'package.json';
import storage from '@/renderer/scripts/utils/storage';
import EventEmitter from '@/common/EventEmitter';
import {
  IS_BROWSER,
  IS_DEV,
  IS_DEV_PUBLISH,
  IS_TEST,
  IS_PRE,
  IS_PUBLISH,
  IS_PRIVATE,
  IS_PC,
  HOST_PREFIX,
} from '@/common/constant';
import i18n from '@/locales/i18n';
import { windowEnvs } from '@/renderer/scripts/utils/ConstantConfig/PrivateConfig';

enum EventMap {
  SMHHostChanged = 'SMHHostChanged',
  SMHHostInit = 'SMHHostInit',
}
class HttpUrl extends EventEmitter {
  static readonly defaultCDNHost = 'tced-static.tencentsmh.cn'; // 默认静态资源 host

  static readonly defaultWebHost = 'pan.tencent.com'; // 默认网页项目host

  static readonly defaultSMHHost = 'api.tencentsmh.cn'; // 默认SMH请求host

  static readonly defaultInternalSMHHost = 'pan.woa.com'; // 默认私有化服务器 host

  public internalSMHHost = ''; // 私有化服务器 host （网页项目host与SMH请求host合并为同一个）

  // 需要注意公有云网页项目host与SMH请求host非同一个
  private SMHHost = 'api.tencentsmh.cn'; // SMH请求host

  private _webHost = 'pan.tencent.com'; // 网页项目host

  private _protocol = 'https';

  get protocol() {
    return this.getProtocol();
  }
  EVENT_MAP = EventMap;

  /**
   * @description 服务器域名设置
   * @param {string} url
   * @memberof HttpUrl
   */
  set SMHUrl(url: string) {
    const urlExp = /^(https|http):\/\/([^\s]+)/;
    if (!urlExp.test(url)) throw new Error('InvalidUrl');
    const { protocol, host } = HttpUrl.formatUrl(url);
    this._protocol = protocol;
    this.internalSMHHost = host;
    this.SMHHost = host;
    if (this.SMHHost.includes('/')) {
      this._protocol = 'http';
      this.internalSMHHost = '';
      this.SMHHost = '';
      throw new Error('InvalidUrl');
    }
    storage.set(storage.StoreKey.INTERNAL_SMHURL, url);
    this.emit(this.EVENT_MAP.SMHHostChanged);
  }
  get SMHUrl() {
    const host = this.getSMHHost();
    return `${this.protocol}://${host}`;
  }
  get wssSMHUrl() {
    const host = this.getSMHHost();
    // websocket协议
    const wss = this.protocol === 'https' ? 'wss' : 'ws';
    return `${wss}://${host}`;
  }
  get webUrl() {
    const host = this.getWebHost();
    return `${this.protocol}://${host}`;
  }
  get staticCDNUrl() {
    let host = HttpUrl.defaultCDNHost;
    if (IS_PRIVATE) {
      host = this.getWebHost();
    }
    return `${this.protocol}://${host}`;
  }
  get staticCDNURLMap() {
    const host = this.staticCDNUrl;
    const suffix = `_v=${pkg.version}`;
    return {
      productLogoUrl: `${host}/tools/images/disk-logo-with-text${i18n.locale === 'en-US' ? '-en' : ''}.svg?${suffix}`,
      productWemeetLogoUrl: `${host}/tools/images/logo-with-text-in-wemeet${
        i18n.locale === 'en-US' ? '-en' : ''
      }.svg?${suffix}`,
      // 私有化统一去掉Tencent
      userManualUrl: !IS_PRIVATE
        ? `${host}/tools/TencentCloudDrive_User_Manual_v3.pdf`
        : `${host}/tools/User_Manual_v3.pdf`,
      errorImgUrl: `${host}/tools/images/error-img.png`,
      userTemplateUrl: `${host}/tools/userTemplate.xlsx`,
      userPasswordTemplateUrl: `${host}/tools/userTemplate-password.xlsx`,
    };
  }

  get webHost() {
    return this._webHost;
  }

  constructor() {
    super();
    this.forceUpdateUrl();
    if (IS_PC && IS_PRIVATE) {
      this.emit(this.EVENT_MAP.SMHHostInit);
    }
  }
  private static formatUrl(url: string) {
    const urlExp = /^(https|http):\/\/([^\s]+)/;
    const match = url.match(urlExp) || [];
    const [, protocol = 'https', host = ''] = match;
    return {
      protocol,
      host,
    };
  }
  private static getHostFromStorage() {
    // 私有化 PC 端需要从 storage 里取出用户自行设定的服务器域名
    let SMHUrl = storage.get<string>(storage.StoreKey.INTERNAL_SMHURL);
    if (windowEnvs.host && IS_PRIVATE && !SMHUrl) {
      SMHUrl = windowEnvs.host;
    }

    return HttpUrl.formatUrl(SMHUrl);
  }
  static getHostPrefix() {
    let prefix = '';
    if (IS_DEV || IS_DEV_PUBLISH) {
      prefix = '.test';
    } else if (IS_PRE) {
      prefix = '.prepub';
    } else if (IS_TEST) {
      // 支持测试域名比如test1.pan.tencent.com 则取出前缀test1
      const { host } = window.location;
      const includeDefaultHost = host.includes(HttpUrl.defaultWebHost) && host !== HttpUrl.defaultWebHost;
      const pcHostPrefix = HOST_PREFIX || 'test';
      const hostPrefix = IS_BROWSER && includeDefaultHost ? window.location.host.split('.')[0] : pcHostPrefix;
      prefix = `.${hostPrefix}`;
    }
    return prefix;
  }
  static getCurrentEnv() {
    if (IS_DEV || IS_DEV_PUBLISH) return 'dev';
    if (IS_TEST) return 'test';
    if (IS_PRE) return 'pre';
    if (IS_PUBLISH) return 'publish';
    return '';
  }
  private getProtocol() {
    if (!IS_PRIVATE) {
      this._protocol = 'https';
    } else if (IS_BROWSER) {
      const browserProtocal = window.location.protocol.split(':');
      this._protocol = browserProtocal[0] as string;
    } else if (!this.internalSMHHost) {
      // 私有化时未初始化（即从本地取一次 protocol）
      this._protocol = HttpUrl.getHostFromStorage().protocol || 'http';
    }
    return this._protocol;
  }
  private getSMHHost() {
    const prefix = HttpUrl.getHostPrefix();
    if (!IS_PRIVATE) {
      if (prefix) {
        this.SMHHost = `api${prefix}.tencentsmh.cn`;
      } else {
        this.SMHHost = HttpUrl.defaultSMHHost;
      }
    } else if (IS_BROWSER) {
      this.SMHHost = IS_DEV ? HttpUrl.defaultInternalSMHHost : window.location.host;
    } else if (!this.internalSMHHost) {
      // 私有化时未初始化（即从本地取一次 host）
      this.internalSMHHost = HttpUrl.getHostFromStorage().host;
      this.SMHHost = this.internalSMHHost;
    }
    return this.SMHHost;
  }
  private getWebHost() {
    if (IS_BROWSER) {
      // 浏览器直接用当前域名
      this._webHost = IS_DEV && IS_PRIVATE ? HttpUrl.defaultInternalSMHHost : window.location.host;
    } else if (!IS_PRIVATE) {
      if (IS_DEV || IS_DEV_PUBLISH) this._webHost = `dev.${HttpUrl.defaultWebHost}`;
      if (IS_TEST) this._webHost = `test.${HttpUrl.defaultWebHost}`;
      if (IS_PRE) this._webHost = `pre.${HttpUrl.defaultWebHost}`;
      if (IS_PUBLISH) this._webHost = `${HttpUrl.defaultWebHost}`;
    } else if (!this.internalSMHHost) {
      this.internalSMHHost = HttpUrl.getHostFromStorage().host;
      this._webHost = this.internalSMHHost;
    } else {
      this._webHost = this.internalSMHHost;
    }
    return this._webHost;
  }

  /** 强制更新SMHUrl 避免私有化取上次的值 */
  forceUpdateUrl() {
    this.internalSMHHost = '';
    // 第一步： 初始化协议
    this.getProtocol();
    // 第二步： 初始化SMH host
    this.getSMHHost();
    // 第三步： 初始化网页 host
    this.getWebHost();
  }
}
export default HttpUrl;
